import { useCallback, useRef, useState, useEffect } from "react";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useMst } from "../../../stores/main";
import { pixelToFrame } from "../utils/timelineHelpers";
import type { TimelineLayerInfo } from "../utils/layerHierarchy";
import {
  getLayerTimingCustomMods,
  normalizeAssetId,
} from "../utils/timingModHelpers";
import {
  clampLayerEdgeDragDelta,
  clampLeftEdge,
  clampRightEdge,
  getLayerEdgeDragDeltaBounds,
} from "./dragUtils";
import { useAnimationJsonEdit } from "./useAnimationJsonEdit";
import type { SelectedLayerInfo } from "./useLayerBodyDrag";
import type { LayerDragPreviewLayer } from "../../../stores/TimelineStore";

type LayerEdge = "left" | "right";

interface DragState {
  isDragging: boolean;
  edge: LayerEdge | null;
  initialIp: number;
  initialOp: number;
  previewFrame: number;
  minDelta: number;
  maxDelta: number;
  hasMoved: boolean;
}

const initialDragState: DragState = {
  isDragging: false,
  edge: null,
  initialIp: 0,
  initialOp: 0,
  previewFrame: 0,
  minDelta: 0,
  maxDelta: 0,
  hasMoved: false,
};

function toLayerLocalFrame(
  layer: TimelineLayerInfo,
  absoluteFrame: number
): number {
  return absoluteFrame - (layer.timelineOffset ?? 0);
}

function removeSelectionsCoveredByAncestor(
  selections: SelectedLayerInfo[]
): SelectedLayerInfo[] {
  return selections.filter(
    (selection) =>
      !selections.some(
        (candidate) =>
          candidate !== selection &&
          selection.layer.instancePath.startsWith(
            `${candidate.layer.instancePath}::`
          )
      )
  );
}

function toPreviewLayers(
  selections: SelectedLayerInfo[]
): LayerDragPreviewLayer[] {
  return selections.map((info) => ({
    layerUid: info.layer.uid,
    layerName: info.layer.nm,
    assetId: normalizeAssetId(info.layer.assetId),
    instancePath: info.layer.instancePath,
  }));
}

function updateLayerEdgeTiming(
  workingAnimation: unknown,
  capturedLayers: SelectedLayerInfo[],
  edge: LayerEdge,
  delta: number
): boolean {
  let hasJsonChanges = false;

  for (const layerInfo of capturedLayers) {
    const targetLayer = layerInfo.layer;
    const targetTiming = layerInfo.resolvedTiming;
    const timingUpdate =
      edge === "left"
        ? {
            ip: toLayerLocalFrame(targetLayer, targetTiming.ip + delta),
            st: toLayerLocalFrame(targetLayer, targetTiming.st + delta),
          }
        : {
            op: toLayerLocalFrame(targetLayer, targetTiming.op + delta),
          };
    const applied = motionAPI.apply.updateLayerTiming(
      workingAnimation as any,
      {
        assetId: normalizeAssetId(targetLayer.assetId),
        layerName: targetLayer.nm,
        layerUid: targetLayer.uid,
      },
      timingUpdate,
      {
        includeNestedLayers: false,
      }
    );

    if (applied.changes.length) {
      hasJsonChanges = true;
    }
  }

  return hasJsonChanges;
}

export interface ResolvedTiming {
  ip: number;
  op: number;
  st: number;
}

export interface UseLayerEdgeDragReturn {
  isDragging: boolean;
  dragEdge: LayerEdge | null;
  previewFrame: number | null;
  startDrag: (e: React.MouseEvent, edge: LayerEdge) => void;
}

export function useLayerEdgeDrag(
  layer: TimelineLayerInfo,
  compositionStart: number,
  resolvedTiming: ResolvedTiming,
  selectedLayers: SelectedLayerInfo[] = [],
  isLayerSelected: boolean = false
): UseLayerEdgeDragReturn {
  const { timelineStore, modsStore, undoStore } = useMst();
  const { zoom, scrollOffset } = timelineStore;
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const [dragState, setDragState] = useState<DragState>(initialDragState);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const rafIdRef = useRef<number | null>(null);
  const tracksAreaRef = useRef<HTMLElement | null>(null);
  const capturedLayersRef = useRef<SelectedLayerInfo[]>([]);

  const startDrag = useCallback(
    (e: React.MouseEvent, edge: LayerEdge) => {
      e.preventDefault();
      e.stopPropagation();

      // Find the tracks area for coordinate calculation
      tracksAreaRef.current = (e.currentTarget as HTMLElement).closest(
        ".timeline-tracks-area"
      ) as HTMLElement;

      undoStore.onGestureStart();

      if (isLayerSelected && selectedLayers.length > 1) {
        capturedLayersRef.current = removeSelectionsCoveredByAncestor(
          selectedLayers
        );
      } else {
        capturedLayersRef.current = [{ layer, resolvedTiming }];
      }

      const { minDelta, maxDelta } = getLayerEdgeDragDeltaBounds(
        capturedLayersRef.current.map((info) => info.resolvedTiming),
        edge
      );

      // Use resolved timing values (from mods) as initial values
      setDragState({
        isDragging: true,
        edge,
        initialIp: resolvedTiming.ip,
        initialOp: resolvedTiming.op,
        previewFrame: edge === "left" ? resolvedTiming.ip : resolvedTiming.op,
        minDelta,
        maxDelta,
        hasMoved: false,
      });

      timelineStore.setLayerEdgeDragPreview({
        layers: toPreviewLayers(capturedLayersRef.current),
        edge,
        delta: 0,
      });
      document.body.classList.add("timeline-edge-dragging");
    },
    [
      isLayerSelected,
      layer,
      resolvedTiming,
      selectedLayers,
      timelineStore,
      undoStore,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds.isDragging || !tracksAreaRef.current) return;

      // Cancel any pending RAF to avoid duplicate updates
      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const currentDs = dragStateRef.current;

        const rect = tracksAreaRef.current!.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const frame =
          pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;
        const rawFrame = Math.round(frame);
        const rawDelta =
          currentDs.edge === "left"
            ? rawFrame - currentDs.initialIp
            : rawFrame - currentDs.initialOp;
        const delta = clampLayerEdgeDragDelta(
          rawDelta,
          currentDs.minDelta,
          currentDs.maxDelta
        );
        let clampedFrame =
          currentDs.edge === "left"
            ? currentDs.initialIp + delta
            : currentDs.initialOp + delta;

        // Keep the existing single-layer guard as a final sanity check.
        if (currentDs.edge === "left") {
          clampedFrame = clampLeftEdge(clampedFrame, currentDs.initialOp);
        } else {
          clampedFrame = clampRightEdge(clampedFrame, currentDs.initialIp);
        }

        const clampedDelta =
          currentDs.edge === "left"
            ? clampedFrame - currentDs.initialIp
            : clampedFrame - currentDs.initialOp;

        setDragState((prev) => ({
          ...prev,
          previewFrame: clampedFrame,
          hasMoved: prev.hasMoved || clampedDelta !== 0,
        }));

        if (currentDs.edge) {
          timelineStore.setLayerEdgeDragPreview({
            layers: toPreviewLayers(capturedLayersRef.current),
            edge: currentDs.edge,
            delta: clampedDelta,
          });
        }
      });
    },
    [zoom, scrollOffset, compositionStart, timelineStore]
  );

  const handleMouseUp = useCallback(() => {
    const ds = dragStateRef.current;
    if (!ds.isDragging) return;

    // Cancel any pending RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const delta =
      ds.edge === "left"
        ? ds.previewFrame - ds.initialIp
        : ds.previewFrame - ds.initialOp;

    if (ds.hasMoved && delta !== 0) {
      const capturedLayers = capturedLayersRef.current;
      const legacyTimingMods = capturedLayers.flatMap((layerInfo) =>
        getLayerTimingCustomMods(modsStore.mods?.slice() ?? [], layerInfo.layer)
      );

      if (ds.edge === "left") {
        applyAnimationJsonEdit(
          (workingAnimation) =>
            updateLayerEdgeTiming(
              workingAnimation,
              capturedLayers,
              "left",
              delta
            ),
          { manageUndo: false }
        );
      } else if (ds.edge === "right") {
        applyAnimationJsonEdit(
          (workingAnimation) =>
            updateLayerEdgeTiming(
              workingAnimation,
              capturedLayers,
              "right",
              delta
            ),
          { manageUndo: false }
        );
      }

      legacyTimingMods.forEach((mod) => {
        modsStore.removeMod(mod);
      });
    }

    undoStore.onGestureEnd();

    setDragState(initialDragState);
    document.body.classList.remove("timeline-edge-dragging");
    timelineStore.clearLayerEdgeDragPreview();
    capturedLayersRef.current = [];
  }, [applyAnimationJsonEdit, modsStore, timelineStore, undoStore]);

  // Document-level event listeners
  useEffect(() => {
    if (!dragState.isDragging) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("timeline-edge-dragging");
      timelineStore.clearLayerEdgeDragPreview();
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp, timelineStore]);

  return {
    isDragging: dragState.isDragging,
    dragEdge: dragState.edge,
    previewFrame: dragState.isDragging ? dragState.previewFrame : null,
    startDrag,
  };
}
